import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Lock } from "lucide-react";
import InstagramIcon from "@/components/InstagramIcon";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";

// Instagram Chat: people who messaged the selected branch's Instagram account.
// Messages arrive through Meta's webhook (saved by the backend). Staff can reply
// here; Instagram only allows a reply within 24 hours of the person's last message.

const POLL_MS = 8000;

function timeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { day: "2-digit", month: "short" }) +
        " " +
        d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function personName(c) {
  return c.name || (c.username ? `@${c.username}` : "Instagram user");
}

export default function InstagramChat() {
  const { activeBranchId } = useBranch();
  const [convs, setConvs] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadList = useCallback(async () => {
    if (!activeBranchId) return;
    try {
      const { data } = await api.get("/instagram/conversations", {
        params: { branch_id: activeBranchId },
      });
      setConvs(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load Instagram chats");
    } finally {
      setLoadingList(false);
    }
  }, [activeBranchId]);

  const loadDetail = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const { data } = await api.get(`/instagram/conversations/${cid}/messages`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not open this chat");
    }
  }, []);

  useEffect(() => {
    setSelectedId(null);
    setDetail(null);
    setLoadingList(true);
    loadList();
  }, [activeBranchId, loadList]);

  useEffect(() => {
    const t = setInterval(() => {
      loadList();
      if (selectedId) loadDetail(selectedId);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [loadList, loadDetail, selectedId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detail?.messages?.length]);

  const openChat = (cid) => {
    setSelectedId(cid);
    setDetail(null);
    loadDetail(cid);
  };

  const send = async () => {
    const body = text.trim();
    if (!body || !selectedId || sending) return;
    setSending(true);
    try {
      await api.post(`/instagram/conversations/${selectedId}/reply`, { text: body });
      setText("");
      await loadDetail(selectedId);
      loadList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Message not sent");
    } finally {
      setSending(false);
    }
  };

  const conv = detail?.conversation;
  const canReply = !!conv?.can_reply;

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <InstagramIcon className="h-6 w-6" />
        <h1 className="text-2xl font-semibold text-white">Instagram Chat</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-4 h-[calc(100vh-170px)] min-h-[420px]">
        {/* List of people */}
        <div className="border border-white/10 rounded-lg bg-white/5 overflow-y-auto">
          {loadingList ? (
            <div className="p-6 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : convs.length === 0 ? (
            <p className="p-6 text-sm text-slate-400">
              No Instagram messages for this branch yet. New messages appear here by themselves.
            </p>
          ) : (
            convs.map((c) => (
              <button
                key={c.id}
                onClick={() => openChat(c.id)}
                className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/10 ${
                  selectedId === c.id ? "bg-white/10" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-white truncate">{personName(c)}</span>
                  <span className="text-xs text-slate-400 shrink-0">{timeLabel(c.last_message_at)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <span className="text-xs text-slate-400 truncate">{c.last_text}</span>
                  {c.unread_count > 0 && (
                    <span className="text-xs bg-pink-600 text-white rounded-full px-2 py-0.5 shrink-0">
                      {c.unread_count}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Chat */}
        <div className="border border-white/10 rounded-lg bg-white/5 flex flex-col min-h-0">
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
              Choose a person on the left.
            </div>
          ) : !detail ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-white/10 text-white font-medium">
                {personName(conv)}
                {conv.username && conv.name ? (
                  <span className="ml-2 text-xs text-slate-400">@{conv.username}</span>
                ) : null}
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {detail.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[75%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                        m.direction === "outbound" ? "bg-pink-700 text-white" : "bg-white/10 text-white"
                      }`}
                    >
                      {m.text}
                      <div className="text-[10px] opacity-60 mt-1">
                        {timeLabel(m.created_at)}
                        {m.direction === "outbound" && m.sent_by && m.sent_by !== "instagram" ? ` · ${m.sent_by}` : ""}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {canReply ? (
                <div className="p-3 border-t border-white/10 flex gap-2">
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    rows={2}
                    maxLength={1000}
                    placeholder="Type a reply"
                    className="flex-1 border border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md px-3 py-2 text-sm focus:outline-none"
                  />
                  <button
                    onClick={send}
                    disabled={sending || !text.trim()}
                    className="px-4 rounded-md bg-pink-600 hover:bg-pink-500 disabled:opacity-50 text-white flex items-center gap-2 text-sm"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send
                  </button>
                </div>
              ) : (
                <div className="p-3 border-t border-white/10 text-xs text-slate-400 flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Instagram only allows a reply within 24 hours of their last message. Reply from the Instagram app, or wait for them to message again.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
